// cuisineDossiers — flavor signatures per cuisine, at the resolution
// the 53-cuisine frame library deserves. Each dossier tells the
// generator HOW a tradition thinks about taste: its aromatic base,
// its fat, where acid comes from, what kind of heat, where depth
// (umami) lives, the balance logic that holds a plate together, when
// seasoning happens (phases), how a dish finishes, and the named
// ingredients that anchor authenticity (guanciale-not-bacon class).
//
// These are COACHING, not constraint: every prompt injection ends
// with the departure clause — the tradition's rails exist so the
// model departs from them deliberately, never accidentally. This is
// the dream-mode-safe shape of authenticity (see
// feedback_protect_dream_mode / feedback_gastronomy_over_code_accuracy).
//
// tier: "curated" = hand-written, owner-reviewable voice.
//       "derived" = AI-drafted for the long tail (added later via the
//       enrichment pattern; regenerable, curated always wins).
//
// Consumed by generate-recipe (sketch + final prompt injection via
// dossierBlock) — keep each dossier compact: the WHOLE block should
// render under ~180 words so it steers without drowning the prompt.

export const CUISINE_DOSSIERS = {
  sichuan: {
    tier: "curated",
    base: "ginger, garlic, scallion — with doubanjiang fried in oil until the oil stains red",
    fat: "chile oil and rapeseed; the oil IS a seasoning, not a medium",
    acid: "Chinkiang black vinegar as a palate reset, not a dressing",
    heat: "layered and named: má (numbing Sichuan peppercorn) + là (fruity dried erjingtiao/facing-heaven chiles) — never generic hot",
    umami: "doubanjiang, douchi (fermented black bean), soy",
    balance: "numbing-hot-savory with a rounding pinch of sugar; vinegar resets the tongue between bites",
    phases: "toast peppercorns + chiles in low oil first; fry the bean paste until the oil reddens; aromatics next; sauces last",
    finish: "fresh scallion, roasted-ground peppercorn dust, a spoon of the red oil",
    anchors: "Pixian doubanjiang not generic chile paste; Sichuan peppercorn not black pepper; Chinkiang not rice vinegar",
  },
  cantonese: {
    tier: "curated",
    base: "ginger and scallion, garlic used sparingly — the wok's breath (wok hei) is itself an aromatic",
    fat: "neutral oil hot enough to shimmer; lard for depth; sesame only as a finishing perfume",
    acid: "restraint — a whisper of red vinegar with seafood; freshness plays acid's role",
    heat: "minimal; white pepper's sharpness, not chile",
    umami: "light + dark soy in ratio, oyster sauce, dried shiitake and scallop steeped back to life",
    balance: "clarity above all — season to reveal the main ingredient's own sweetness, never to cover it",
    phases: "aromatics flashed in a smoking wok seconds before the protein; sauce pre-mixed and added in one motion; slurry last",
    finish: "hot oil poured sizzling over raw scallion-ginger; drops of sesame oil off heat; white pepper",
    anchors: "Shaoxing wine not sake; real oyster sauce; steam whole fish — never boil it",
  },
  korean: {
    tier: "curated",
    base: "garlic in quantities that look like a mistake, scallion, ginger",
    fat: "neutral for the pan; toasted sesame oil is a FINISHER — it dies under high heat",
    acid: "fermentation sourness (ripe kimchi carries the acid), rice vinegar in banchan",
    heat: "gochugaru's fruity warmth and gochujang's sweet fermented burn — heat with flavor attached",
    umami: "doenjang, guk-ganjang (soup soy), fish sauce or saeujeot in the background",
    balance: "fermented depth against fresh crunch; sweet-heat against cool rice; every meal needs a raw, crisp counterpoint",
    phases: "fry the pastes in fat to wake them before liquid; garlic mid-cook; sesame oil and seeds off heat",
    finish: "toasted sesame, scallion threads, an egg yolk melting over hot rice",
    anchors: "doenjang not miso (coarser, funkier — they are not interchangeable); gochugaru not crushed red pepper",
  },
  thai: {
    tier: "curated",
    base: "pounded paste — lemongrass, galangal, garlic, shallot, makrut lime peel, cilantro root — pounded, not blended, when it matters",
    fat: "coconut cream cracked until the oil separates; neutral oil for the wok",
    acid: "lime squeezed at the very end; tamarind where acid must survive cooking",
    heat: "fresh bird's eye for bright fire, dried chiles inside pastes for depth",
    umami: "fish sauce is the backbone; shrimp paste (kapi) hides inside curry pastes",
    balance: "hot, sour, salty, sweet held in genuine tension — if one wins, the dish loses; taste and re-balance at the end, always",
    phases: "crack the coconut cream, fry the paste in it until fragrant and the oil returns; liquids; protein; lime and herbs OFF heat",
    finish: "lime, Thai basil or cilantro, fried shallots, prik nam pla on the table",
    anchors: "galangal not ginger; makrut lime not bay; palm sugar not white sugar; fish sauce brand matters",
  },
  vietnamese: {
    tier: "curated",
    base: "shallot, garlic, lemongrass; charred onion and ginger for broths",
    fat: "light hand — neutral oil, scallion oil (mỡ hành) as a garnish-fat",
    acid: "lime, and the pickle plate (đồ chua) doing constant background work",
    heat: "fresh chile offered raw at the table, restrained in the pot",
    umami: "fish sauce (nước mắm) first and last; caramel (nước màu) in braises reads as savory depth, not dessert",
    balance: "featherweight freshness — a mountain of raw herbs — against deep savory-caramel braise or broth; the contrast IS the cuisine",
    phases: "make the caramel first for kho dishes; aromatics fast; herbs never cooked, heaped raw at serving",
    finish: "the herb plate (mint, cilantro, tía tô), lime wedge, nước chấm mixed fresh",
    anchors: "nước mắm quality decides the dish; rau răm and tía tô named, not 'mixed herbs'",
  },
  japanese_country: {
    tier: "curated",
    base: "dashi is the foundation of everything — kombu and katsuobushi (or iriko) drawn gently, never hard-boiled; negi and ginger above it",
    fat: "nearly none; sesame as an accent, the fryer reserved for its own dishes",
    acid: "rice vinegar, yuzu, and the pickle course (tsukemono) carrying brightness for the whole meal",
    heat: "shichimi tōgarashi at the table; wasabi and karashi as pointed accents",
    umami: "dashi, rustic barley miso, soy, mirin — layered quietly",
    balance: "umami-forward and gently sweet; season to REVEAL the ingredient — restraint is the technique",
    phases: "dashi first and treated tenderly; miso whisked in off the boil (boiled miso goes dull); mirin and soy simmered to marry",
    finish: "yuzu zest, mitsuba or shiso, a pinch of sanshō",
    anchors: "real dashi, never bouillon; mirin not sugar-and-sake substitute; white rice is a course, not a base",
  },
  persian: {
    tier: "curated",
    base: "onions fried patiently to deep gold — the base of the entire cuisine — then turmeric bloomed into the fat",
    fat: "butter or ghee with oil; butter carries saffron",
    acid: "sour is the signature: dried lime (limoo amani), pomegranate molasses, verjuice, sumac, barberries",
    heat: "almost none — warmth comes from spice (advieh), never chile burn",
    umami: "long-cooked depth; kashk (fermented whey) where richness is wanted",
    balance: "sour-savory stews against sweet rice crusts; herbs used by the BUNCH as a vegetable, not a garnish",
    phases: "onions to gold before anything else; herbs for ghormeh sabzi fried dark and slow; saffron bloomed in hot water and added late",
    finish: "bloomed saffron spooned over rice, barberries flashed in butter, the tahdig crust presented crackling-side up",
    anchors: "limoo amani not lemon juice; saffron bloomed, never thrown in dry; advieh not curry powder",
  },
  lebanese: {
    tier: "curated",
    base: "onion softened gently, garlic pounded raw into sauces (toum) or warmed briefly — never bitter-browned",
    fat: "olive oil poured generously to FINISH; clarified butter for pastry and rice",
    acid: "lemon on nearly everything, sumac's dry tang, pomegranate molasses where sweetness should shadow the sour",
    heat: "mild — Aleppo-pepper warmth, not fire",
    umami: "tahini's roasted depth, yogurt's tang, well-browned meat",
    balance: "bright lemon-garlic against nutty tahini against cool yogurt — three poles, most dishes stand between two of them",
    phases: "seven-spice with the meat from the start; garlic raw or barely warmed; lemon at the end so it stays alive",
    finish: "a pool of olive oil, sumac dust, pine nuts fried golden in butter, torn mint",
    anchors: "Lebanese 7-spice (baharat) not garam masala; labneh not sour cream; sumac not lemon zest",
  },
  turkish: {
    tier: "curated",
    base: "onion melted slowly in fat with biber salçası (pepper paste) fried until it darkens and smells sweet",
    fat: "olive oil for vegetable dishes (zeytinyağlı), butter for finishing — paprika butter sizzled at the end is a signature",
    acid: "lemon, vinegar with garlic over fried vegetables, sour plum in some stews; yogurt's lactic tang counts",
    heat: "pul biber (Maraş) fruity-warm; isot/Urfa smoky, almost raisiny — choose by name",
    umami: "tomato AND pepper pastes double-fried, yogurt, grilled-meat drippings into bread",
    balance: "sweet cooked peppers + warm spice + cooling garlic-yogurt; dried mint bridges hot and cold dishes",
    phases: "pastes fried in fat first until brick-dark; dried mint rubbed in near the end; butter-paprika bloomed seconds before serving",
    finish: "sizzling paprika butter poured over yogurt, dried mint, Urfa flakes",
    anchors: "biber salçası not plain tomato paste; Maraş and Urfa named, not 'chili flakes'; yogurt is a sauce, not a side",
  },
  moroccan: {
    tier: "curated",
    base: "onion, garlic, ginger sweated with the spices — cumin, coriander, sweet paprika, saffron, cinnamon in savory roles",
    fat: "olive oil; a knob of smen (aged butter) where funk and depth belong",
    acid: "preserved lemon — the RIND, sliced fine — plus tomato; fresh lemon only at the table",
    heat: "gentle; harissa served alongside so each eater sets their own fire",
    umami: "the tagine's own slow reduction; olives' brine; almond depth in sweet-savory dishes",
    balance: "the sweet-savory signature (apricots, dates, honey against lamb and spice) held honest by preserved-lemon salt-acid and olive brine",
    phases: "spices bloom with the onions; saffron steeps in the braising liquid; fruit joins late to keep its shape",
    finish: "preserved lemon slivers, a shower of cilantro-parsley, toasted almonds; honey where sweet is the point",
    anchors: "preserved lemon not fresh; ras el hanout is a HOUSE blend — name a few components; couscous steamed, never just soaked",
  },
  oaxacan: {
    tier: "curated",
    base: "char is the aromatic: tomatoes, tomatillos, garlic, onion blackened on a dry comal; dried chiles toasted just to fragrance",
    fat: "lard (asiento) for honesty; neutral oil acceptable",
    acid: "tomatillo brightness inside sauces; lime at the table",
    heat: "layered dried chiles by NAME — pasilla oaxaqueña, guajillo, chilhuacle — fruit, raisin, and smoke before raw fire",
    umami: "toasted chile seeds, avocado leaf's anise depth, aged cheese",
    balance: "char-bitter + chile-fruit + herbal camphor (hoja santa, epazote) — mole logic is chords, not single notes",
    phases: "toast chiles seconds-only (scorched = bitter mole); char vegetables truly black; fry the blended sauce until it 'breaks' and darkens",
    finish: "queso fresco crumbled, raw onion + cilantro, lime; a final spoon of the sauce's own fat",
    anchors: "epazote and hoja santa by name; masa not flour; quesillo (Oaxaca string cheese) not mozzarella",
  },
  cajun: {
    tier: "curated",
    base: "the trinity — onion, celery, green bell pepper — with garlic arriving after",
    fat: "the roux IS the dish: oil or lard and flour taken patiently to peanut-butter, then brick, then chocolate brown by intent",
    acid: "vinegar-based hot sauce; tomatoes belong to Creole cousins, not the Cajun pot",
    heat: "cayenne, black, and white pepper in three registers — heat that builds, not stabs",
    umami: "andouille's smoky render, stock built from shrimp shells, filé's root depth",
    balance: "dark-roux bitterness and smoke against the trinity's sweetness, cut by vinegar heat at the table",
    phases: "roux first and unhurried; trinity into the hot roux to stop its cooking; spice early, adjusted again at the end",
    finish: "scallion and parsley over rice; filé stirred in OFF heat (boiled filé goes ropy); hot sauce bottle on the table",
    anchors: "andouille not kielbasa; the roux's COLOR names the dish — say which brown; filé never boiled",
  },
  sicilian: {
    tier: "curated",
    base: "olive oil, garlic, and anchovies melted until they vanish — the invisible foundation",
    fat: "fruity olive oil, generously; breadcrumbs toasted in it stand in for cheese",
    acid: "agrodolce — wine vinegar warmed with sugar; lemon and salt-packed capers everywhere",
    heat: "a pinch of dried chile, no more",
    umami: "anchovy, bottarga, pecorino, estrattu (sun-dried tomato paste)",
    balance: "sweet-sour (agrodolce) + saline (caper, olive, anchovy) + the sweetness of slow onions — the island's Arab inheritance on a plate",
    phases: "anchovies dissolve in warm oil before anything; saffron into the pasta water; currants and pine nuts warmed, not cooked",
    finish: "toasted breadcrumbs (muddica atturrata) over pasta instead of cheese; wild fennel fronds; lemon zest",
    anchors: "pecorino not parmesan; currants + pine nuts are a signature pair; capers salt-packed and rinsed",
  },
  provencal: {
    tier: "curated",
    base: "olive oil and garlic, warmed never harshly browned; thyme, rosemary, and savory used fresh and with restraint",
    fat: "olive oil entirely — butter is for Paris",
    acid: "tomato, lemon, a glass of dry rosé or white into the pan",
    heat: "none — garlic's warmth carries the dish",
    umami: "anchovy (anchoïade), olive tapenade, tomatoes melted to jam over hours",
    balance: "resinous herbs against sweet summer vegetables against saline olive-anchovy — sun, salt, and garden",
    phases: "garlic gentled in oil; herbs early for daubes, late for grills; saffron blooms in fish broth",
    finish: "pistou (basil-garlic-oil, no pine nuts) stirred in off heat; chopped niçoise olives; lemon zest",
    anchors: "pistou not pesto; niçoise olives by name; herbes de Provence fresh-built, not a dusty jar",
  },
  basque: {
    tier: "curated",
    base: "onion and leek melted slow, choricero pepper flesh stirred in",
    fat: "olive oil; the pil-pil emulsion — cod gelatin and oil swirled into silk — is the cuisine's signature fat-craft",
    acid: "txakoli's spritz, cider vinegar in the refrito, piperrade's tomatoes",
    heat: "guindilla gentle; piment d'Espelette as a finishing warmth",
    umami: "salt cod (bacalao), jamón ends dropped into stocks, grilled-over-coals char",
    balance: "sea-saline against pepper-sweet against wood smoke — a grill culture that treats fish like treasure",
    phases: "pil-pil emulsified low with a constant swirl; peppers stewed patiently; whole fish over coals fast and basted",
    finish: "garlic-vinegar refrito spooned sizzling over grilled fish; parsley; Espelette dust",
    anchors: "choricero and piquillo by name, never 'red pepper'; Espelette not paprika; bacalao properly desalted",
  },

  // ── Derived tier — AI-drafted for the long tail. Regenerable;
  // curated always wins on conflict. Same schema, same compactness.
  yunnan: {
    tier: "derived",
    base: "ginger, garlic, fresh and pickled chiles; Dai dishes lean on lemongrass and mint",
    fat: "modest — lard or neutral oil; ham fat renders into vegetables",
    acid: "pickled mustard greens and sour bamboo shoot inside dishes; lime in Dai salads",
    heat: "fresh green and red chiles, bright not heavy; dried-chile dips on the side",
    umami: "wild mushrooms (porcini, matsutake in season) and Xuanwei ham — the twin pillars",
    balance: "borderland freshness — Southeast Asian herbs and sour against Chinese wok craft; lighter than Sichuan next door",
    phases: "ham and mushrooms early to season the fat; pickles mid-cook; herbs and lime raw at the end",
    finish: "fresh mint and cilantro, a chile-lime dip alongside, crisped ham slivers",
    anchors: "Xuanwei ham not generic ham; zhe'ergen (fish-mint root) is beloved, polarizing, and real; rubing cheese exists — grill it",
  },
  hunan: {
    tier: "derived",
    base: "garlic, ginger, scallion in smoking oil — more garlic than Sichuan, no numbing peppercorn",
    fat: "rendered pork fat and neutral oil; la rou (smoked bacon) frying is the aroma of the cuisine",
    acid: "pickled chiles and pickled long beans — sour arrives fused to the heat",
    heat: "gan la — dry, sharp, unrounded: fresh chiles stir-fried, duojiao (salted chopped chile) steamed over fish",
    umami: "la rou's smoke, douchi (fermented black bean), fermented radish",
    balance: "hot-sour-salty with NO sugar rounding — sweetness reads Cantonese here; the burn is the point",
    phases: "chiles and aromatics hit smoking oil first; douchi with them; sauce fast and light at the end",
    finish: "fresh chopped chiles and scallion over everything; the steaming liquor spooned back over fish",
    anchors: "duojiao not chile sauce; la rou smoked over wood, not supermarket bacon; no Sichuan peppercorn — that's the neighbor's move",
  },
  shanghainese: {
    tier: "derived",
    base: "ginger and scallion — scallions fried slowly to brown perfume (cong you) is a foundation",
    fat: "lard for richness, scallion-infused oil, the sheen of a reduced braise",
    acid: "restrained — Chinkiang vinegar alongside xiaolongbao and hairy crab, not in the pot",
    heat: "essentially none; white pepper at most",
    umami: "light plus dark soy in ratio, dried shrimp, salted pork simmered with fresh (yan du xian's double-pork logic)",
    balance: "sweet is structural: rock sugar and soy reduced to a lacquer (hong shao) — concentrated, glossy, unapologetic",
    phases: "rock sugar melted to caramel first; dark soy for color early, light for salt; Shaoxing generous; reduce uncovered",
    finish: "braising liquid reduced till it coats and shines; scallion oil poured hot over noodles",
    anchors: "rock sugar not granulated; hong shao sauce must lacquer the meat — thin sauce is failure; Shaoxing wine is not optional",
  },
  burmese: {
    tier: "derived",
    base: "shallot, garlic, ginger, turmeric fried in generous oil until the oil returns clear (si pyan) — that's doneness",
    fat: "peanut oil, unapologetically pooled; the returned oil is served, not skimmed",
    acid: "tamarind, lime, tomatoes cooked down; pickled tea's gentle sour",
    heat: "moderate dried chile in the pot; balachaung (fried chile-shrimp relish) brings heat on the side",
    umami: "ngapi (fermented shrimp paste), dried shrimp, fish sauce — layered, not hidden",
    balance: "the salad logic (thoke): sour-salty-crunchy-nutty in one bowl — fried garlic, peanuts, toasted chickpea flour against ferment funk",
    phases: "onion-garlic-turmeric fried till the oil separates before anything else; chickpea flour toasted dry; lime and crisp bits at assembly",
    finish: "crisped shallots and garlic, toasted chickpea flour, lime, a spoon of balachaung",
    anchors: "laphet (fermented tea leaf) is a national dish, not a garnish; ngapi not plain fish sauce; mohinga is breakfast",
  },
  filipino: {
    tier: "derived",
    base: "ginisa — garlic first in fat, then onion, then tomato; garlic rice (sinangag) says how central garlic is",
    fat: "pork fat and neutral oil; coconut milk where Bicol speaks (chile-coconut laing)",
    acid: "the signature — cane or coconut vinegar in braises, calamansi over everything, tamarind sour in sinigang",
    heat: "mostly gentle; siling labuyo fierce in Bicol dishes and vinegar dips",
    umami: "patis (fish sauce), bagoong (fermented shrimp paste), soy in adobo, braised-down pork",
    balance: "sour-salty is the home key, sweet plays openly against it; sawsawan dips let each eater retune the plate",
    phases: "vinegar braises simmer UNSTIRRED until the raw edge cooks off; ginisa base opens most pots; calamansi at the table",
    finish: "toasted garlic bits, calamansi halves, a vinegar-chile-soy sawsawan mixed to taste",
    anchors: "cane vinegar not distilled white; calamansi not lime; adobo is a method, not one recipe",
  },
  indonesian: {
    tier: "derived",
    base: "bumbu — shallot, garlic, candlenut (kemiri), galangal, turmeric, chiles pounded to a paste",
    fat: "coconut oil and coconut milk; the paste is fried until the oil separates (tumis) before liquid",
    acid: "tamarind (asam jawa) and lime; sambals carry their own bright edge",
    heat: "sambal is a genre, not a condiment — raw-pounded, or fried with terasi; fresh red chiles in the bumbu",
    umami: "terasi (fermented shrimp paste) toasted before use, kecap manis, dried salted fish, palm-sugar caramel depth",
    balance: "sweet (palm sugar, kecap manis) against chile fire and aromatic paste; kerupuk crunch against rich coconut",
    phases: "fry the ground bumbu patiently till the oil returns; rendang reduces until the split coconut oil fries the beef",
    finish: "bawang goreng (crisp fried shallots), a fresh raw sambal, kerupuk on the side",
    anchors: "kemiri named, not macadamia; terasi toasted, never raw from the block; kecap manis is not soy plus sugar",
  },
  malaysian: {
    tier: "derived",
    base: "rempah — shallot, garlic, ginger, chiles, belacan pounded and fried until pecah minyak (the oil splits)",
    fat: "coconut milk and oil; the rempah's returned oil carries the dish",
    acid: "tamarind (asam jawa) and asam gelugor (dried garcinia) — laksa asam's sour is fruit, not vinegar; calamansi",
    heat: "dried chiles ground into rempah for depth, sambal belacan pounded fresh for fire",
    umami: "belacan toasted till it smokes, ikan bilis (dried anchovy), dried shrimp",
    balance: "three kitchens braided — Malay, Chinese, Indian; rich coconut against asam sour, fiery sambal against coconut rice",
    phases: "toast the belacan first; fry rempah low until oil splits; coconut milk in stages, reduced between",
    finish: "fried ikan bilis and peanuts, daun kesum (laksa leaf), a spoon of sambal",
    anchors: "belacan toasted, never raw; asam gelugor not vinegar for laksa; daun kesum named — mint is not it",
  },
  goan: {
    tier: "derived",
    base: "onion browned deep, ginger-garlic, and a wet-ground masala — spices ground WITH vinegar or coconut",
    fat: "coconut oil; coconut milk rounds fish curries",
    acid: "the anchor — toddy or coconut vinegar (the Portuguese vinha d'alhos inheritance), kokum in fish curry, tamarind",
    heat: "dried Kashmiri chiles ground into pastes — deep red color, warm not brutal; recheado's vinegar-chile sting",
    umami: "dried shrimp, fermented vinegar depth, well-browned onion",
    balance: "sour-hot masala against coconut sweetness; rice-and-fish-curry is the daily axis",
    phases: "marinate overnight in vinegar-garlic-spice; fry the paste till its raw smell leaves; kokum in late",
    finish: "kokum's dusky sour in the last minutes, fresh coconut slivers, solkadhi alongside",
    anchors: "vindaloo is vinha d'alhos — wine-vinegar and garlic, no potatoes; toddy vinegar not malt; kokum not lemon",
  },
  kerala: {
    tier: "derived",
    base: "the temper — mustard seeds popped in coconut oil, curry leaves crackled in, small red shallots, ginger, green chile",
    fat: "coconut in every register: oil, thick and thin milk pressed separately, fresh grated flesh",
    acid: "kodampuli (smoked Malabar tamarind) for fish — sour with smoke attached; tamarind and tomato elsewhere",
    heat: "green chiles slit lengthwise, native Malabar black pepper, dried red chile in roasted masalas",
    umami: "coconut roasted deep brown and ground (varutharacha) — the dark backbone of the big curries",
    balance: "coconut sweetness against kodampuli smoke-sour against pepper warmth; a fish curry improves overnight in its clay pot",
    phases: "the temper opens or finishes the dish; roast coconut dark for varutharacha; thick coconut milk last, never boiled hard",
    finish: "a fresh spoon of raw coconut oil over fish curry, fried curry leaves",
    anchors: "kodampuli not tamarind for meen curry; curry leaves fresh, never dried; coconut oil IS the flavor",
  },
  bengali: {
    tier: "derived",
    base: "mustard oil heated to smoking then cooled slightly (tames the raw bite); panch phoron tempered whole; ginger, green chile",
    fat: "mustard oil, non-negotiable; ghee spooned over rice and dal",
    acid: "gentle — tomato, tamarind in chutneys; kasundi (fermented mustard) carries a pungent sour-sharp",
    heat: "slit green chiles and mustard's sinus-heat more than chile burn; dried red in the temper",
    umami: "freshwater fish (hilsa, rui) fried then curried; shrimp; posto (poppy-seed paste) depth",
    balance: "the meal progresses bitter-first (shukto) through dal and fish to chutney; a pinch of sugar is structural",
    phases: "smoke the mustard oil first; panch phoron whole into it; fish fried before meeting gravy",
    finish: "a raw drizzle of mustard oil, gondhoraj lime zest, ghee on the dal",
    anchors: "panch phoron wants radhuni not celery seed; hilsa steamed in shorshe (mustard paste); posto is Bengal's own",
  },
  punjabi: {
    tier: "derived",
    base: "onions browned dark, ginger-garlic paste, tomato — the masala bhuna-ed until fat beads at the edges",
    fat: "ghee, butter, cream — dairy is structural; white butter on saag, malai in the gravy",
    acid: "yogurt in marinades and gravies, amchur (dried mango) in chana, pickle sharpness alongside",
    heat: "dried red and fresh green chile, honest but not scorching; garam masala warmth",
    umami: "deep-browned onion, tandoor char, dals simmered overnight (urad and rajma in dal makhani)",
    balance: "dairy richness against tandoor smoke and pickle bite; robust seasoning — timidity reads as failure here",
    phases: "whole cumin blooms in ghee first; bhuna the masala till oil separates; garam masala and kasuri methi at the END",
    finish: "kasuri methi crushed between palms, a swirl of cream, ginger julienne, charcoal dhungar smoke when wanted",
    anchors: "kasuri methi is the signature finisher; dal makhani's butter is structural, not garnish; amchur not lemon in chana",
  },
  kashmiri: {
    tier: "derived",
    base: "two schools — Pandit: hing, dry ginger (saunth), fennel powder, no onion-garlic; wazwan: browned shallots and garlic",
    fat: "mustard oil smoked first; ghee for richness",
    acid: "yogurt, tempered in slowly for yakhni so it never splits; tomato is absent from the classics",
    heat: "Kashmiri chile — crimson color first, gentle warmth second; heat is never the headline",
    umami: "lamb's rendered fat, yogurt reduced to a silk gravy, slow bone-in braises",
    balance: "warm aromatics (fennel, dry ginger, clove, cinnamon) over gentle heat and lactic tang — perfumed, not fiery",
    phases: "smoke the mustard oil; yogurt whisked in off the boil for yakhni; saffron bloomed, added late",
    finish: "Pampore saffron in warm milk over rice, a hing-ghee tarka, dried mint on yakhni",
    anchors: "rogan josh is red from Kashmiri chile and mawal (cockscomb flower), NOT tomato; fennel-saunth is the Pandit signature",
  },
  tunisian: {
    tier: "derived",
    base: "onion, garlic, and tabil (coriander-caraway-garlic-chile) with harissa fried into the base — structural, not condiment",
    fat: "olive oil, enough that the harissa stains it red",
    acid: "tomato double-cooked, preserved lemon, capers' brine",
    heat: "the Maghreb's hottest table — harissa's dried-chile-garlic-caraway burn, built in AND offered raw on top",
    umami: "tomato paste fried dark, canned tuna (an icon here, not a compromise), olives, anchovy",
    balance: "fiery red base against briny capers-olives-tuna against a runny egg's richness",
    phases: "fry harissa and tomato paste till the oil reddens; tabil with them; eggs poached into the sauce last",
    finish: "an egg cooked soft in the sauce (ojja), raw harissa, olive oil, capers",
    anchors: "harissa house-built — chile, garlic, caraway, coriander; caraway is Tunisia's tell where Morocco reaches for cinnamon; brik's yolk stays runny",
  },
  egyptian: {
    tier: "derived",
    base: "onion browned in samna (clarified butter), garlic, cumin and coriander as the workhorse pair",
    fat: "samna baladi for cooking; olive oil raw over ful and salads",
    acid: "lime (not lemon) over ful, tomato, the garlic-vinegar dakka spooned over koshari",
    heat: "mild in the pot; shatta (chile paste) on the table for self-service fire",
    umami: "slow tomato sauce, mountains of crisp fried onion, duqqa's toasted nut-spice depth",
    balance: "earthy legumes (ful, lentils, koshari) lifted by sharp garlic-vinegar-lime and crackling fried onion",
    phases: "the taqliya — garlic and ground coriander fried in samna — hits molokhia SIZZLING at the end, and the pot gasps",
    finish: "crisp fried onions over koshari, dakka, olive oil and lime on ful, duqqa for dipping bread",
    anchors: "molokhia's silky-slick texture is the point, not a flaw; taqliya not raw garlic; samna not plain butter",
  },
  ethiopian: {
    tier: "derived",
    base: "red onions cooked down DRY first — no fat until they collapse — then niter kibbeh and berbere build the wat",
    fat: "niter kibbeh — clarified butter infused with besobela (sacred basil) and koseret; half the cuisine's aroma",
    acid: "injera itself — teff's fermented sour is the plate's acid, under everything",
    heat: "berbere's slow deep burn (chile, fenugreek, korarima); mitmita sharper and hotter, for kitfo and dipping",
    umami: "long-cooked onion-berbere concentration, shiro (seasoned chickpea flour), injera's ferment floor",
    balance: "fiery kai wat beside mild turmeric alicha on one injera; cool ayib cheese calms the berbere",
    phases: "onions dry-cooked first; berbere fried gently in niter kibbeh — scorched berbere turns bitter; meat braises in the sauce",
    finish: "a spoon of niter kibbeh melting over the wat, ayib alongside, mitmita for the brave",
    anchors: "niter kibbeh not plain butter; berbere is a built blend, not chile powder; korarima not green cardamom",
  },
  senegalese: {
    tier: "derived",
    base: "onions in improbable volume — yassa is a mountain of them stewed with mustard and lime; garlic-parsley-chile pounded to rof",
    fat: "peanut oil; palm oil where the dish calls its name",
    acid: "lime in the yassa marinade, tamarind (dakhar), vinegar",
    heat: "scotch bonnet — often left WHOLE in the pot for perfume, pounded into rof when fire is meant",
    umami: "the funk trio: guedj (fermented dried fish), yete (fermented sea snail), netetou (fermented locust bean) — intentional, load-bearing",
    balance: "deep onion-mustard-sour or tomato-rice richness against fermented-sea depth and chile perfume",
    phases: "fish stuffed with rof before frying; yassa onions stewed long; rice cooks in the stew's own liquid (thiebou dienne)",
    finish: "the xoon — crusted rice from the pot bottom — served proudly; lime; mustard-onion sauce spooned over",
    anchors: "guedj and netetou named, never omitted 'to clean it up'; broken rice for thieb; yassa needs mustard AND lime",
  },
  nigerian: {
    tier: "derived",
    base: "tatashe (red bell), scotch bonnet, tomato, onion blended, then FRIED DOWN until the mix darkens and the oil separates",
    fat: "unrefined red palm oil for egusi, banga, native stews; neutral oil for the party pot",
    acid: "little — the fried-down tomato's residual edge; fermentation carries tang elsewhere (ogi)",
    heat: "scotch bonnet (ata rodo) built in, not optional; suya's yaji brings ginger-peanut heat",
    umami: "iru (fermented locust bean) and ground dried crayfish — the flavor floor; stockfish above them",
    balance: "deep-fried red-stew base plus smoke (party jollof's firewood note is prized) against ferment funk",
    phases: "fry the blended pepper base until raw sourness leaves and oil rises; iru early; jollof steams low, lidded with foil",
    finish: "suya re-dusted with yaji after grilling; the smoky pot-bottom rice claimed first",
    anchors: "iru and crayfish named; palm oil is not substitutable in egusi; yaji is kuli-kuli (peanut) based; jollof grains separate, never mushy",
  },
  south_african: {
    tier: "derived",
    base: "onion browned with Cape Malay curry spice (turmeric, coriander, cinnamon) on one axis; wood fire (braai) on the other",
    fat: "beef and boerewors drippings over coals; butter-apricot bastes for snoek",
    acid: "apricot and vinegar in the sweet-sour Cape Malay register; blatjang (chutney) alongside",
    heat: "gentle curry warmth; peri-peri (African bird's eye) where real fire is wanted",
    umami: "braai char, biltong's spiced cure (coriander seed), chakalaka's tomato-bean depth",
    balance: "fruit-sweet against curry spice (bobotie's raisins in curried custard-topped mince) — sweetness is legitimate, not a lapse",
    phases: "bobotie's spiced mince cooks before the egg custard bakes over it; potjiekos layers and is NEVER stirred; boerewors never pricked",
    finish: "blatjang and sambals alongside, pap with chakalaka, apricot-butter glaze on snoek off the coals",
    anchors: "boerewors is coriander-forward and mostly meat by law; potjie stirring is sacrilege; braai burns wood, not gas",
  },
  peruvian: {
    tier: "derived",
    base: "aderezo — red onion, garlic, ají amarillo paste fried down slow until it deepens",
    fat: "neutral oil for the aderezo and chifa's wok-breath; huancaína's cheese-ají cream where silk is wanted",
    acid: "lime, immediate and central — leche de tigre cures ceviche in minutes, not hours",
    heat: "by name: ají amarillo (sunny, fruity), ají panca (dark, smoky, mild), rocoto (fierce) — each a different register",
    umami: "soy via Nikkei and chifa heritage (lomo saltado's wok), queso fresco, huacatay (black mint)",
    balance: "coastal lime-chile brightness against Andean earth (potato, choclo); a crema usually mediates",
    phases: "fry the aderezo until it darkens; ceviche assembles at the LAST moment; lomo saltado flash-fries over the highest flame",
    finish: "leche de tigre spooned over, cancha (toasted corn) for crunch, slivered red onion rinsed sharp",
    anchors: "ají amarillo is the cuisine's color, not generic chile; chifa and Nikkei are heritage, not fusion gimmicks",
  },
  yucatan: {
    tier: "derived",
    base: "recados — spice pastes; recado rojo (achiote, garlic, oregano, spices) ground with sour orange is the flagship",
    fat: "lard, honestly used; the pib (earth oven) renders it into everything",
    acid: "naranja agria (sour orange) everywhere — marinades, pickled onions, the squeeze at the table",
    heat: "habanero — fruity and violent — but held AT THE TABLE: xnipec salsa and pickled onions; the cooking itself is gentle",
    umami: "pit smoke, achiote's earthy depth, charred chiltomate (roasted-tomato salsa)",
    balance: "earthy achiote and smoke against sour-orange brightness; each eater adds habanero fire per bite",
    phases: "meat marinates overnight in recado; cochinita wraps in banana leaf and cooks low for hours; onions pickle ahead in sour orange",
    finish: "cebolla encurtida (pickled red onion) over everything, xnipec, warm corn tortillas",
    anchors: "recado rojo not 'adobo'; banana leaf is a flavor, not a wrapper; habanero never jalapeño; sub sour orange knowingly (lime plus orange)",
  },
  puerto_rican: {
    tier: "derived",
    base: "sofrito — recao (culantro), ají dulce, onion, garlic, peppers blended fresh and fried; it opens rice, beans, and stews",
    fat: "lard stained gold-red with achiote (annatto) — steep the seeds in hot fat, strain, cook in the color",
    acid: "understated — vinegar-oregano in escabeche, pique (vinegar-chile) at the table",
    heat: "essentially none in the pot — ají dulce smells like habanero but carries no fire; pique is opt-in",
    umami: "ham or tocino fried with the sofrito, alcaparrado (olives, capers, pimento), tomato sauce",
    balance: "herbal-savory sofrito depth and briny olives against sweet plantain and starchy rice",
    phases: "achiote oil first; sofrito fried in it before anything; alcaparrado in with the simmer",
    finish: "mofongo mashed hot with garlic and chicharrón; ajilimójili or mojito isleño over fish",
    anchors: "recao not plain cilantro; ají dulce not bell pepper; pegao (crisped rice bottom) is prized, not burnt",
  },
  cuban: {
    tier: "derived",
    base: "sofrito of onion, green bell pepper, garlic with the Cuban chord — cumin, oregano, bay",
    fat: "lard traditionally, olive oil now; maduros fry sweet in either",
    acid: "mojo criollo — naranja agria (sour orange) with raw garlic and oregano; lime where sour orange runs out",
    heat: "none — and that's identity, not timidity; black pepper is the ceiling",
    umami: "long braises shredded into their own juices (ropa vieja), olives, capers, raisins in picadillo",
    balance: "garlicky-sour mojo against sweet fried plantain against briny-raisiny picadillo — sweet, salt, and sour share one plate",
    phases: "pork marinates overnight in mojo; sofrito opens beans and rice; a splash of vinegar and pinch of sugar finish frijoles negros",
    finish: "mojo spooned over lechón, raw onion on vaca frita, lime, white rice under everything",
    anchors: "sour-orange mojo, not vinegar; cumin-oregano-bay is the signature chord; no chile — heat would be a different island",
  },
  lowcountry: {
    tier: "derived",
    base: "onion, celery, bell pepper opened in rendered smoked pork — but rice is the true foundation (Gullah Geechee rice culture)",
    fat: "bacon grease and butter; shrimp-shell stock enriched with both",
    acid: "lemon with seafood, hot-pepper vinegar on greens, a cider-vinegar edge in red rice",
    heat: "cayenne in moderation; the vinegar bottle carries the heat to the table",
    umami: "shrimp stock from the shells, smoked sausage and country ham, oyster liquor never discarded",
    balance: "sweet Carolina Gold rice and corn against marsh salinity and pork smoke; benne's nutty undertone",
    phases: "render smoked pork first; rice cooks as a pilau in stock, grains separate; shrimp in the final minutes",
    finish: "scallions, toasted benne seeds, lemon, a shake of pepper vinegar",
    anchors: "Carolina Gold rice named — this is a rice cuisine; perloo not jambalaya; shrimp-and-grits gravy starts with shells, not just cream",
  },
  quebecois: {
    tier: "derived",
    base: "onion sweated in rendered salt pork or lard; winter spices (clove, cinnamon, allspice) in SAVORY roles",
    fat: "lard, butter, salt-pork renderings; poutine gravy's sheen",
    acid: "cider vinegar, pickled beets alongside the rich plates",
    heat: "none — black pepper and clove warmth stand in",
    umami: "pork stock, dark poultry-beef gravy (poutine's sauce brune), long-baked beans with salt pork",
    balance: "fat and maple sweetness against vinegar pickles; spiced meat pies against plain boiled potato",
    phases: "tourtière's mince simmers with its clove-cinnamon-savory blend before baking; fèves au lard bake overnight low with maple and salt pork",
    finish: "real maple where sweetness belongs, summer savory (sarriette), curds added to poutine at room temperature so they squeak",
    anchors: "summer savory is the province's herb; curds must squeak — shredded mozzarella is an insult; tourtière is warm-spiced, never sage-stuffing",
  },
  pacific_nw: {
    tier: "derived",
    base: "leek and shallot gentled in butter; restraint — the larder (salmon, oysters, chanterelles, berries) is the argument",
    fat: "butter, salmon's own richness, hazelnut oil as a nutty accent",
    acid: "berries in savory roles (huckleberry with game), apple cider, lemon over shellfish, rhubarb in season",
    heat: "nearly none; horseradish with smoked fish at most",
    umami: "wild mushrooms (chanterelle, morel, matsutake by season), oyster liquor, kelp, alder-smoked salmon",
    balance: "forest against tide — mushrooms, fir tips, hazelnuts meeting salmon, Dungeness, oysters; season to reveal, not cover",
    phases: "mushrooms dry-seared before butter goes in; salmon cooked gently and pulled at medium; shellfish barely cooked at all",
    finish: "spruce or fir tips, toasted hazelnuts, a berry gastrique with game, brown butter and lemon over crab",
    anchors: "alder is the smoking wood (Coast Salish tradition), not hickory; Dungeness named; Oregon hazelnuts, not almonds",
  },
  ligurian: {
    tier: "derived",
    base: "the gentlest Italian kitchen — garlic barely warmed in oil; young basil, marjoram, and pine nuts do the talking",
    fat: "Taggiasca olive oil, delicate and sweet; nut richness (walnut sauce, pine nuts) where others reach for cream",
    acid: "light — white wine, a little lemon; freshness carries brightness",
    heat: "none",
    umami: "Parmigiano mixed with pecorino sardo in pesto, anchovy, dried porcini in tocco, prescinsêua's tang",
    balance: "herb-forward and vegetable-led; richness arrives by nut and cheese, never weight — the lightest hand on the peninsula",
    phases: "pesto pounded cold and NEVER cooked — hot pasta and a spoon of its water loosen it off heat; focaccia brined before baking",
    finish: "raw Taggiasca oil, torn marjoram, pesto folded in off the flame",
    anchors: "pesto genovese never sees heat; marjoram not oregano; trofie with green beans and potato is the complete dish, not a garnish",
  },
  sardinian: {
    tier: "derived",
    base: "olive oil, garlic, parsley; saffron grown on the island (San Gavino) threads through pasta and broth",
    fat: "olive oil; lard and pecorino richness in the shepherd interior",
    acid: "dry white vernaccia with fish, tomato (fresh and sun-dried), lemon light",
    heat: "gentle — black pepper, a little dried chile",
    umami: "bottarga di muggine (cured mullet roe), pecorino sardo, fregola's toast, dried tomato",
    balance: "an island of two kitchens — shepherd (lamb, pecorino, pane carasau) and coast (bottarga, clams); saffron is the golden thread",
    phases: "fregola is toasted before simmering — the toast IS the flavor; saffron in early; porceddu roasts slow with myrtle",
    finish: "bottarga grated over at the table — never cooked, it turns harsh; raw oil; myrtle with roasts",
    anchors: "pecorino sardo, gentler than romano — not interchangeable; bottarga off heat only; pane carasau named",
  },
  alsatian: {
    tier: "derived",
    base: "onions melted blond in lard or goose fat — choucroute and tarte flambée both start white, never browned hard",
    fat: "lard, goose fat, butter; crème fraîche with fromage blanc as the tarte flambée base",
    acid: "the ferment — sauerkraut braised in Riesling; vinegar warm over bacon salads; the wine's own cut",
    heat: "none in the pot; horseradish and sharp mustard on the side",
    umami: "smoked pork in ranks — hock, bacon, Strasbourg and Montbéliard sausages; Munster's washed-rind funk",
    balance: "ferment-sour kraut against rich smoked pork and potato; Riesling acidity keeps the cream honest",
    phases: "kraut rinsed lightly, braised in Riesling with juniper and bay; meats staggered by cooking time; tarte flambée blazes fast",
    finish: "mustard and horseradish alongside, a splash of kirsch, cumin seeds with the Munster",
    anchors: "choucroute braises in Riesling, not water; juniper named; tarte flambée is crème fraîche and onion — never a cheese pizza",
  },
  burgundian: {
    tier: "derived",
    base: "mirepoix and lardons rendered together; then wine — a full bottle of Pinot reduced is an ingredient, not a splash",
    fat: "butter above all, lardon fat for searing, beurre manié to gloss the sauce",
    acid: "red wine's reduction, wine vinegar, Dijon's sharpness",
    heat: "none beyond Dijon's sting",
    umami: "long braises in wine and brown stock, Époisses funk, garlic-parsley butter over escargots",
    balance: "deep wine-braise richness cut by Dijon and persillade (parsley-garlic) freshness",
    phases: "sear hard in lardon fat; flour dusted and toasted; wine reduced before stock joins; the garnish trio glazed separately, added late",
    finish: "beurre manié to gloss; the bourguignonne trio — pearl onions, mushrooms, lardons — on top, never stewed gray",
    anchors: "a Pinot you would drink; Dijon not yellow mustard; the garnish cooked apart separates bourguignon from beef stew",
  },
  catalan: {
    tier: "derived",
    base: "sofregit — onion (often with tomato) cooked slower and darker than seems right; 45 minutes is normal",
    fat: "arbequina olive oil; lard in pastry and some rice dishes",
    acid: "tomato, vinegar in escabetx and xató dressing, bitter orange in some pots",
    heat: "gentle — the nyora pepper is sweet-earthy, not hot; black pepper",
    umami: "picada (pounded nuts, fried bread, garlic, saffron) stirred in at the end; anchovy, salt cod",
    balance: "mar i muntanya — sea and mountain in one pot (chicken with prawns); sweet-savory runs legitimate (raisins with pine nuts)",
    phases: "sofregit opens the dish, picada closes it — the two bookends; the picada binds and thickens in the last ten minutes",
    finish: "picada worked in, allioli alongside, parsley",
    anchors: "sofregit and picada are the cuisine's brackets — name both; nyora for romesco; true allioli is garlic and oil alone",
  },
  galician: {
    tier: "derived",
    base: "spare — onion and bay in the pot; the ajada (warm garlic-paprika oil) IS the seasoning",
    fat: "olive oil; unto (cured pork fat) melting into caldo galego",
    acid: "almost none — the cuisine trusts salt, sweet pimentón, and the sea; Albariño does acid's work in the glass",
    heat: "pimentón picante as an option on pulpo; otherwise gentle",
    umami: "the Atlantic — octopus, mussels, percebes; lacón (cured pork shoulder); grelos' bitter edge as counterweight",
    balance: "sweet just-cooked seafood and potato against smoky-sweet paprika oil and pork-fat depth; grelos cut the richness",
    phases: "octopus dipped thrice into the boil, simmered tender, rested; pimentón blooms in warm oil OFF heat — it scorches instantly",
    finish: "coarse salt, pimentón, raw olive oil over warm pulpo and cachelos; Padrón peppers blistered with flaky salt",
    anchors: "pulpo á feira takes NO lemon — salt, paprika, oil, done; unto named in caldo; pimentón never fried dark",
  },
  andalusian: {
    tier: "derived",
    base: "garlic, stale bread, and olive oil — the cold-emulsion trinity (gazpacho, salmorejo, ajoblanco); onion-pepper sofrito for stews",
    fat: "olive oil in both registers: raw in the cold soups, and a frying culture that deep-fries IN it",
    acid: "Sherry vinegar — the signature; lemon over fritura; ripe tomato",
    heat: "minimal — a whisper of pimentón, black pepper",
    umami: "jamón ibérico and its fat, mojama (salt-cured tuna), anchovy, fino and oloroso sherry deglazing the pan",
    balance: "cold garlicky silk against hot fried crunch; saline jamón against sherry's oxidative depth",
    phases: "salmorejo emulsified cold; fritura in hot clean olive oil, small batches, fine flour only, salted immediately",
    finish: "chopped jamón and egg over salmorejo, lemon with fried fish, fino splashed into sizzling shrimp",
    anchors: "Sherry vinegar, never balsamic; salmorejo is thick and bread-bound, not thin gazpacho; fried fish wears flour, never batter",
  },
  georgian_country: {
    tier: "derived",
    base: "onion and garlic beneath a wall of herbs used by the bunch — cilantro, dill, tarragon, purple basil, summer savory",
    fat: "walnuts, ground to a paste, ARE the fat — satsivi, pkhali, bazhe all ride on them; butter in the west",
    acid: "fruit-sour — tkemali (sour plum sauce), pomegranate, unripe grape; vinegar stays rare",
    heat: "adjika — a chile-garlic-herb paste, hot AND green-fragrant; khmeli suneli warms without burning",
    umami: "walnut richness, sulguni's briny stretch, blue fenugreek's haunting depth",
    balance: "walnut fat against fruit acid against the raw-herb mountain on every table",
    phases: "walnuts ground fresh with garlic and spice, loosened with broth; adjika added late or at the table",
    finish: "pomegranate seeds, cilantro, Imeretian saffron dust; khachapuri's butter and yolk land at the last second",
    anchors: "blue fenugreek (utskho suneli) and dried marigold — no curry-aisle substitutes; tkemali not tamarind; sulguni not mozzarella",
  },
  polish: {
    tier: "derived",
    base: "włoszczyzna — carrot, leek, celeriac, and parsley ROOT — under the stocks; onion in butter or lard everywhere else",
    fat: "butter, smalec (lard with cracklings), and śmietana (sour cream) finishing half the dishes",
    acid: "the ferment shelf — sauerkraut and its juice, dill-pickle brine (ogórkowa), żur's soured rye starter, buttermilk",
    heat: "none — horseradish and sharp mustard carry the edge",
    umami: "dried borowik (porcini) and their soaking liquor, smoked kiełbasa, bacon, kraut's depth",
    balance: "pork fat and starch against ferment-sour and fresh dill; śmietana mediates between them",
    phases: "dried mushrooms soaked, their liquor into the pot; bigos improves over days; śmietana tempered off the boil",
    finish: "skwarki (cracklings) and fried onion over pierogi, dill in blizzard quantity, a cold spoon of śmietana",
    anchors: "parsley root and celeriac in the base; żur needs true fermented rye zakwas; marjoram is the pork herb",
  },
  hungarian: {
    tier: "derived",
    base: "a mass of onion melted blond in fat — the pot comes OFF heat for the paprika, which scorches in seconds",
    fat: "lard (zsír) or goose fat; tejföl (sour cream) as the rich finisher",
    acid: "tejföl's lactic tang, the obligatory savanyúság (pickle plate) beside anything rich, lecsó's tomato-pepper brightness",
    heat: "paprika by name — édes (sweet) for body, csípős for fire; fresh wax peppers on top",
    umami: "paprika bloomed in lard, smoked kolbász and speck, onions cooked to sweetness",
    balance: "paprika-lard richness and onion sweetness against sour-cream tang and pickle sharp",
    phases: "paprika bloomed off heat, liquid in fast to protect it; pörkölt stews nearly dry; tejföl tempered in at the end (habarás)",
    finish: "a swirl of tejföl, wax pepper rings and tomato over pörkölt, csipetke pinched into gulyás",
    anchors: "gulyás is a soup, pörkölt is the stew; Szeged or Kalocsa paprika, fresh — stale paprika is dust",
  },
  russian_country: {
    tier: "derived",
    base: "zazharka — onion and carrot fried in butter, stirred into the soup near the end; bay, garlic, dill",
    fat: "butter, smetana on everything, unrefined sunflower oil (nutty, raw) over salads and black bread",
    acid: "the ferment pantry — sauerkraut, brined cucumbers AND their rassol (brine) poured into rassolnik, kvass under okroshka",
    heat: "none — fierce Russian mustard and khren (horseradish) wait on the side",
    umami: "dried forest mushrooms, salted and smoked fish, bone broths that ripen overnight (shchi is better tomorrow)",
    balance: "fat and starch against ferment-sour brightness; raw dill freshens; soup is the meal's center",
    phases: "buckwheat toasted dry before simmering; shchi rests a day; brine in at the end",
    finish: "a spoon of smetana in every bowl, a snowfall of dill, sunflower oil over vinegret",
    anchors: "smetana named; rassol (pickle brine) is an ingredient, not waste; grechka toasted and fluffy; black bread is structural",
  },
  scandinavian: {
    tier: "derived",
    base: "restraint — butter and leek or shallot; dill, white pepper, allspice and bay in the cures and broths",
    fat: "butter (browned to brunet smør for fish), cream, cured fish's own richness",
    acid: "the 1-2-3 pickle brine (vinegar, sugar, water), pickled herring, lingonberry against rich meat, buttermilk",
    heat: "none — sweet dill-mustard sauce with gravlax, horseradish, white pepper",
    umami: "cured and smoked fish, aged cheese (Västerbotten), Swedish ansjovis — spiced sprats, not Mediterranean anchovy",
    balance: "clean sweet-sour and cure-salt against butter and cream; rye and crispbread ground every plate",
    phases: "gravlax cures two days under weight in salt-sugar-dill; warm pickle brine over vegetables; meatballs finish in cream gravy",
    finish: "brown butter spooned over, dill and chives, lingonberries alongside, crisp rye crumbs",
    anchors: "Jansson's temptation depends on Swedish ansjovis (sprats) — real anchovy wrecks it; gravlax is a cure, not a smoke; lingonberry not cranberry",
  },
  british_pub: {
    tier: "derived",
    base: "onions — slow-cooked into gravies, battered into rings, pickled whole beside a ploughman's; beef dripping underneath",
    fat: "beef dripping (chips demand it), suet in pastry and puddings, salty butter",
    acid: "malt vinegar on chips, pickled onions and tangy brown pickle with cheese, Worcestershire's tamarind edge",
    heat: "English mustard — fierce, used in dabs; horseradish with beef; white pepper",
    umami: "Worcestershire, roast-tin gravy, aged cheddar, stout braising the pie filling, anchovy hiding in some butters",
    balance: "rich brown depth (gravy, ale braise, dripping) against sharp pickle and mustard — the pickle tray is structural",
    phases: "Yorkshire batter rests cold, then hits smoking dripping; chips fry twice; pie fillings braise a day ahead",
    finish: "gravy poured at the table, malt vinegar and flaky salt on chips, parsley sauce over ham",
    anchors: "beef dripping not vegetable oil for chips; English mustard, not Dijon; Worcestershire named where a savory splash is meant",
  },
};

// The departure clause every injection carries — this is what keeps
// dossiers dream-mode-safe: rails to depart from deliberately.
const DEPARTURE_CLAUSE =
  "These are the tradition's rails — you may absolutely depart from " +
  "them for a deliberate creative concept, but depart ON PURPOSE and " +
  "let the rationale say so; never drift by accident.";

// Keyword detection for typed prompts ("sichuan mapo tofu" should get
// the Sichuan dossier without a concept frame). Distinctive tokens
// only — nothing that false-positives on dish names from other
// traditions. Signature dishes count as cuisine intent.
const DOSSIER_KEYWORDS = {
  sichuan:          ["sichuan", "szechuan", "mapo"],
  cantonese:        ["cantonese", "char siu", "wok hei"],
  korean:           ["korean", "kimchi", "bulgogi", "gochujang", "bibimbap"],
  thai:             ["thai", "pad see", "tom yum", "tom kha", "larb"],
  vietnamese:       ["vietnamese", "banh mi", "pho ", "bún", "nuoc cham"],
  japanese_country: ["japanese", "izakaya", "donburi", "miso soup", "teriyaki"],
  persian:          ["persian", "iranian", "tahdig", "ghormeh", "fesenjan"],
  lebanese:         ["lebanese", "shawarma", "fattoush", "kibbeh"],
  turkish:          ["turkish", "kofte", "köfte", "pide", "menemen"],
  moroccan:         ["moroccan", "tagine", "chermoula", "ras el hanout"],
  oaxacan:          ["oaxaca", "mole ", "tlayuda"],
  cajun:            ["cajun", "gumbo", "jambalaya", "etouffee", "étouffée"],
  sicilian:         ["sicilian", "sicily", "caponata", "pasta alla norma"],
  provencal:        ["provencal", "provençal", "provence", "ratatouille", "bouillabaisse"],
  basque:           ["basque", "pil pil", "piperade", "txakoli"],
  // Derived tier
  yunnan:           ["yunnan", "crossing the bridge", "er kuai"],
  hunan:            ["hunan", "duojiao"],
  shanghainese:     ["shanghainese", "shanghai", "hong shao", "xiaolongbao", "lion's head"],
  burmese:          ["burmese", "myanmar", "mohinga", "laphet"],
  filipino:         ["filipino", "philippine", "sinigang", "kare-kare", "lumpia"],
  indonesian:       ["indonesian", "rendang", "nasi goreng", "gado-gado"],
  malaysian:        ["malaysian", "laksa", "nasi lemak", "char kway teow"],
  goan:             ["goan", "vindaloo", "xacuti", "recheado"],
  kerala:           ["kerala", "keralan", "thoran", "moilee", "appam"],
  bengali:          ["bengali", "panch phoron", "macher jhol", "shorshe"],
  punjabi:          ["punjabi", "dal makhani", "sarson ka saag", "butter chicken"],
  kashmiri:         ["kashmiri", "rogan josh", "yakhni", "wazwan"],
  tunisian:         ["tunisian", "ojja", "brik ", "mechouia"],
  egyptian:         ["egyptian", "koshari", "molokhia", "ful medames"],
  ethiopian:        ["ethiopian", "injera", "berbere", "doro wat"],
  senegalese:       ["senegalese", "yassa", "thieboudienne", "mafe"],
  nigerian:         ["nigerian", "jollof", "egusi", "suya"],
  south_african:    ["south african", "bobotie", "boerewors", "braai", "chakalaka"],
  peruvian:         ["peruvian", "ceviche", "lomo saltado", "aji amarillo", "ají amarillo"],
  yucatan:          ["yucatan", "yucatán", "cochinita", "pibil", "recado"],
  puerto_rican:     ["puerto ric", "mofongo", "pernil", "arroz con gandules"],
  cuban:            ["cuban", "ropa vieja", "vaca frita", "mojo criollo"],
  lowcountry:       ["lowcountry", "low country", "shrimp and grits", "perloo", "she-crab"],
  quebecois:        ["quebec", "québec", "poutine", "tourtiere", "tourtière"],
  pacific_nw:       ["pacific northwest", "cedar plank", "dungeness"],
  ligurian:         ["liguria", "pesto genovese", "trofie", "farinata"],
  sardinian:        ["sardinia", "fregola", "bottarga", "malloreddus"],
  alsatian:         ["alsatian", "alsace", "choucroute", "tarte flambee", "flammekueche"],
  burgundian:       ["burgundian", "burgundy", "bourguignon", "coq au vin"],
  catalan:          ["catalan", "romesco", "escalivada", "mar i muntanya"],
  galician:         ["galicia", "pulpo a feira", "caldo gallego", "caldo galego"],
  andalusian:       ["andalusia", "andalucia", "salmorejo", "gazpacho"],
  georgian_country: ["georgian", "khachapuri", "khinkali", "satsivi"],
  polish:           ["polish", "pierogi", "bigos", "zurek", "żurek"],
  hungarian:        ["hungarian", "goulash", "gulyas", "paprikash"],
  russian_country:  ["russian", "shchi", "pelmeni", "stroganoff"],
  scandinavian:     ["scandinavian", "nordic", "gravlax", "swedish meatball"],
  british_pub:      ["british", "pub ", "fish and chips", "shepherd's pie", "bangers"],
};

// Resolve the dossier for a generation request: the concept frame's
// cuisine pick wins (surprise/roll paths carry { cuisine: {id,...} }),
// then a keyword scan over the typed meal prompt. Null when neither
// binds — the model runs on its own knowledge, exactly as before.
export function dossierForRequest(conceptFrame, mealPrompt) {
  const frameId = conceptFrame?.cuisine?.id;
  if (frameId && CUISINE_DOSSIERS[frameId]) return dossierBlock(frameId);
  const t = ` ${String(mealPrompt || "").toLowerCase()} `;
  for (const [id, kws] of Object.entries(DOSSIER_KEYWORDS)) {
    if (kws.some((k) => t.includes(k))) return dossierBlock(id);
  }
  return null;
}

// Render a dossier as a compact prompt block. Returns null when the
// cuisine has no dossier yet (long tail — derived tier lands later).
export function dossierBlock(cuisineId) {
  const d = CUISINE_DOSSIERS[cuisineId];
  if (!d) return null;
  return [
    `CUISINE SIGNATURE — ${cuisineId.replace(/_/g, " ").toUpperCase()}:`,
    `- Aromatic base: ${d.base}`,
    `- Fat: ${d.fat}`,
    `- Acid: ${d.acid}`,
    `- Heat: ${d.heat}`,
    `- Depth/umami: ${d.umami}`,
    `- Balance logic: ${d.balance}`,
    `- Seasoning phases: ${d.phases}`,
    `- Finishing moves: ${d.finish}`,
    `- Authenticity anchors: ${d.anchors}`,
    DEPARTURE_CLAUSE,
  ].join("\n");
}
